/**
 * Per-episode usage accumulation shared by every client surface (CLI
 * footer, web Context stats). Moved verbatim from the CLI hook
 * (plan agent-todo-context-panel, DEC-5 / INV-10).
 *
 * Each AI-SDK step emits one `usage` frame with per-STEP tokens (not
 * cumulative per-turn). We sum every step, dedup by `(turn_id, step_index)`
 * to protect against reconnect/replay. `cost_micros === null` means the
 * underlying llm_call row was spooled; we count the tokens but skip the
 * cost and bump `pendingCostCount`.
 */

import type { UsageEvent } from "./AgentChatStore.ts";

export interface UsageTotals {
  readonly input: number;
  readonly output: number;
  readonly cacheRead: number;
  readonly cacheWrite: number;
  readonly cacheWrite1h: number;
  readonly reasoning: number;
  readonly costMicros: number;
  readonly pendingCostCount: number;
}

export function emptyTotals(): UsageTotals {
  return {
    input: 0,
    output: 0,
    cacheRead: 0,
    cacheWrite: 0,
    cacheWrite1h: 0,
    reasoning: 0,
    costMicros: 0,
    pendingCostCount: 0,
  };
}

/**
 * Pure reducer — applies one usage event to a running total. `seen` is a
 * mutable set mutated in place; caller owns its lifetime so dedup survives
 * across calls but resets when caller clears it (e.g. on episode change).
 *
 * Returns the same `prev` reference when the event is ignored (wrong episode,
 * duplicate) — callers can use reference equality to avoid redundant renders.
 */
export function applyUsage(
  prev: UsageTotals,
  seen: Set<string>,
  activeEpisodeId: string | null,
  event: UsageEvent,
): UsageTotals {
  if (!activeEpisodeId || event.episode_id !== activeEpisodeId) return prev;
  const key = `${event.turn_id}:${String(event.step_index)}`;
  if (seen.has(key)) return prev;
  seen.add(key);

  const cum = event.cumulative;
  const nextPending =
    event.cost_micros === null
      ? prev.pendingCostCount + 1
      : prev.pendingCostCount;
  const costAdd = event.cost_micros === null ? 0 : event.cost_micros;

  return {
    input: prev.input + cum.input,
    output: prev.output + cum.output,
    cacheRead: prev.cacheRead + cum.cache_read,
    cacheWrite: prev.cacheWrite + cum.cache_write,
    cacheWrite1h: prev.cacheWrite1h + cum.cache_write_1h,
    reasoning: prev.reasoning + cum.reasoning,
    costMicros: prev.costMicros + costAdd,
    pendingCostCount: nextPending,
  };
}
